import { Box, Typography } from "@mui/material";
import i18next from "i18next";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useParams } from "react-router-dom";
import BestPractisesButton from "../../../../components/common/BestPractisesButton/BestPractisesButton.tsx";
import BriaButton from "../../../../components/common/BriaButton/BriaButton";
import BriaTooltip from "../../../../components/common/BriaTooltip/BriaTooltip.tsx";
import LoadingPlaceholder from "../../../../components/common/LoadingPlaceholder/LoadingPlaceholder";
import RouterConstants from "../../../../constants/RouterConstants";
import useSecureNavigate from "../../../../hooks/useSecureNavigate";
import { useAppStore } from "../../../../hooks/useStores";
import TabsLayout from "../../../../layout/TabsLayout/TabsLayout";
import { TgProject, defaultProject } from "../store/tg-projects-store";
import styles from "./TgProjectViewer.module.scss";

export type ViewTgProjectLocationState = {
	projectToView?: TgProject;
};

const TgProjectViewer = () => {
	const { t } = useTranslation("tg", { keyPrefix: "projects.viewer" });
	const navigate = useSecureNavigate();
	const location = useLocation();
	const { projectId } = useParams();
	const { uiStore, tgStore } = useAppStore();
	const { tgProjectStore } = tgStore;
	const pathSegments = location.pathname.split("/");
	const selectedTab = pathSegments[pathSegments.length - 1];
	const projectHeaderFields: (keyof TgProject)[] = useMemo(() => ["ip_medium", "ip_type", "ip_name"], []);

	const subTabs = [RouterConstants.NEW_TG.MODELS.path, RouterConstants.NEW_TG.DATASETS.path];

	// Check if there are any models for the Evaluate button
	const hasAnyModels = tgProjectStore.paginatedModels.items.find((model) => model.status === "Completed");

	useEffect(() => {
		uiStore.hideSideBar();
		uiStore.showBackButton(RouterConstants.NEW_TG.PROJECTS.fullPath, undefined, t("backButton"));

		tgProjectStore.projectToView = location.state?.projectToView || defaultProject;

		(async () => {
			try {
				if (!tgProjectStore.projectToView.id && projectId) {
					const project = await tgProjectStore.getProject(Number(projectId));
					tgProjectStore.projectToView = project;
				}
			} catch (err: any) {
				if (err.response?.status === 404) {
					navigate(RouterConstants.NEW_TG.fullPath);
				}
			}
		})();

		return () => {
			uiStore.showSideBar();
			uiStore.hideBackButton();
			window.history.replaceState({}, "");
		};
	}, []);

	const primaryButtonClick = () => {
		switch (selectedTab) {
			case RouterConstants.NEW_TG.MODELS.path:
				return navigate(
					`${RouterConstants.NEW_TG.PROJECTS.fullPath}/${projectId}/${RouterConstants.NEW_TG.MODELS.createPath}`,
				);
			case RouterConstants.NEW_TG.DATASETS.path:
				return navigate(
					`${RouterConstants.NEW_TG.PROJECTS.fullPath}/${projectId}/${RouterConstants.NEW_TG.DATASETS.createPath}`,
				);
		}
	};

	const secondaryButtonClickHandler = (isBestPractices: boolean = false) => {
		if (selectedTab === RouterConstants.NEW_TG.MODELS.path && !isBestPractices) {
			navigate(
				`${RouterConstants.NEW_TG.PROJECTS.fullPath}/${projectId}/${RouterConstants.NEW_TG.MODELS.path}/${RouterConstants.NEW_TG.MODELS.EVALUATION.path}`,
			);
		}
	};

	const getPrimaryButton = () => {
		switch (selectedTab) {
			case RouterConstants.NEW_TG.MODELS.path:
				return t(`primaryButton.${selectedTab}`);
			case RouterConstants.NEW_TG.DATASETS.path:
				return t(`primaryButton.${selectedTab}`);
		}
	};

	const getSecondaryButton = () => {
		if (selectedTab === RouterConstants.NEW_TG.MODELS.path) {
			return (
				<Box className={styles.modelSecondaryButton}>
					<BestPractisesButton />
					<BriaTooltip
						title={t("secondaryButton.modelsEvaluateTooltip")}
						disable={hasAnyModels !== undefined}
					>
						<BriaButton
							buttonType="secondaryMedium"
							onClick={() => secondaryButtonClickHandler(false)}
							disabled={!hasAnyModels}
						>
							{t(`secondaryButton.modelsEvaluate`)}
						</BriaButton>
					</BriaTooltip>
				</Box>
			);
		}
		return <BestPractisesButton />;
	};

	return (
		<TabsLayout
			className={styles.tabsLayout}
			title={
				<LoadingPlaceholder
					className={`${styles.loadingPlaceholder} ${styles.title}`}
					isLoading={tgProjectStore.loadingProject}
					size={28}
				>
					<BriaTooltip
						className={styles.briaToolTip}
						displayOn="click"
						title={tgProjectStore.projectToView?.project_name || ""}
					>
						<Typography className={styles.title}>
							{tgProjectStore.projectToView?.project_name
								? tgProjectStore.projectToView.project_name.length > 70
									? `${tgProjectStore.projectToView.project_name.slice(0, 70)}...`
									: tgProjectStore.projectToView.project_name
								: ""}
						</Typography>
					</BriaTooltip>

					<Box className={styles.fields}>
						{projectHeaderFields.map((field) => {
							const ipType = tgProjectStore.projectToView.ip_type;
							const shouldRenderField =
								field !== "ip_name" ? true : field === "ip_name" && ipType === "defined_character";

							if (!shouldRenderField || !tgProjectStore.projectToView[field]) return null;

							return (
								<Box className={styles.field}>
									<Typography className={styles.fieldKey}>
										{field === "ip_name"
											? i18next.t(`projects.editor.ipType.types.${ipType}.${field}.label`, {
													ns: "tg",
											})
											: t(`title.${field}`)}
										:
									</Typography>

									{field === "ip_name" ? (
										<BriaTooltip
											className={styles.briaToolTip}
											displayOn="click"
											title={tgProjectStore.projectToView[field]}
										>
											<Typography className={styles.fieldValue}>
												{typeof tgProjectStore.projectToView[field] === "string" &&
												(tgProjectStore.projectToView[field] as string).length > 15
													? `${(tgProjectStore.projectToView[field] as string).slice(
															0,
															15,
													)}...`
													: tgProjectStore.projectToView[field] || ""}
											</Typography>
										</BriaTooltip>
									) : (
										<Typography className={styles.fieldValue}>
											{field === "ip_type"
												? i18next.t(
														`projects.editor.ipType.types.${tgProjectStore.projectToView[field]}.name`,
														{ ns: "tg" },
												)
												: tgProjectStore.projectToView[field]?.toString()}
										</Typography>
									)}
								</Box>
							);
						})}
					</Box>
				</LoadingPlaceholder>
			}
			subTabs={subTabs}
			isLoading={tgProjectStore.loadingProject}
			primaryButtonLabel={getPrimaryButton()}
			primaryButtonClickHandler={primaryButtonClick}
			secondaryButtonLabel={getSecondaryButton()}
			secondaryButtonClickHandler={secondaryButtonClickHandler}
			secondaryButtonType={"secondaryMedium"}
		>
			<Outlet />
		</TabsLayout>
	);
};

export default observer(TgProjectViewer);
